import { WebLayout } from "components/Layout";
import { motion } from "framer-motion";
import { CheckCircle, MapPin, Shield, Tool, Users, Award } from "react-feather";
import { Link } from "react-router-dom";
import aboutImg from "assets/gallery/image_50.jpeg";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.45, ease: "easeOut" },
  }),
};

export function AboutUs() {
  const values = [
    {
      title: "Quality",
      body: "Tight welds, smooth finishing, precise fit-up.",
      icon: Shield,
    },
    {
      title: "Creativity",
      body: "Designs tailored to your space and style.",
      icon: Award,
    },
    {
      title: "Reliability",
      body: "Clear timelines, honest pricing, tidy installs.",
      icon: CheckCircle,
    },
  ];

  const expertise = [
    { k: "Core Skills", v: "Fabrication, welding, grinding, finishing" },
    { k: "Products", v: "Windows, doors, rails, balconies, furniture" },
    { k: "Fittings", v: "Hinges, locks, rollers, handles" },
    { k: "Finishes", v: "Powder coat, galvanizing, paint systems" },
  ];

  return (
    <WebLayout>
      {/* Header */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-gray-300">
              About Us
            </span>
            <h1 className="mt-4 text-4xl font-extrabold text-white md:text-5xl">
              Built for <span className="text-green-400">Strength</span>.
              Crafted for <span className="text-yellow-400">Style</span>.
            </h1>
            <p className="mt-4 text-gray-300 md:text-lg">
              Crutial Touch Art &amp; Crafts Metal Work is a Kampala-based
              workshop dedicated to producing durable, beautiful metalwork that
              upgrades homes and businesses across the region.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro + Values */}
      <section className="bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 py-14 md:py-18 grid gap-10 md:grid-cols-2">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 p-6"
          >
            <h2 className="text-2xl font-bold text-white md:text-3xl">
              Our Story &amp; Mission
            </h2>
            <p className="mt-3 text-gray-300">
              We started with a simple promise: combine precision metalwork with
              thoughtful design. From site measurement to installation, our team
              treats every project like it’s our own—so you get long-lasting
              function and a clean, elevated look.
            </p>
            <p className="mt-3 text-gray-300">
              Our mission is to craft windows, doors, burglarproofing, stairs,
              railings, furniture, and fittings that make your spaces safer,
              stronger, and more beautiful.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="grid gap-4"
          >
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  custom={i}
                  variants={fadeUp}
                  className="flex items-start gap-3 rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 p-5"
                >
                  <span className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-md bg-green-500/15">
                    <Icon size={18} className="text-green-400" />
                  </span>
                  <div>
                    <div className="font-semibold text-white">{v.title}</div>
                    <div className="text-sm text-gray-400">{v.body}</div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Location & Coverage */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-14 md:py-18 grid gap-8 md:grid-cols-3">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="md:col-span-1"
          >
            <h2 className="text-2xl font-bold text-white md:text-3xl">
              Location &amp; Coverage
            </h2>
            <p className="mt-2 text-gray-300">
              <span className="inline-flex items-center gap-2">
                <MapPin size={18} className="text-green-400" /> Kasangati Road,
                Kampala, Opposite Court
              </span>
            </p>
            <p className="mt-2 text-gray-400">
              We serve Kampala and surrounding regions with prompt site visits
              and installations.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center rounded-lg bg-green-500 px-4 py-2 font-semibold text-black hover:brightness-105"
              >
                Request a Quote
              </Link>
              <a
                href="https://wa.me/256779706977"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-lg border border-white/15 px-4 py-2 font-semibold text-white hover:bg-white/10"
              >
                WhatsApp
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="md:col-span-2 overflow-hidden rounded-2xl border border-white/10"
          >
            <iframe
              title="Map to Crutial Touch Art & Crafts"
              src="https://www.google.com/maps?q=Kasangati+Road,+Opposite+Court,+Kampala,+Uganda&output=embed"
              className="h-80 w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </section>

      {/* Team / Expertise */}
      <section className="bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 py-14 md:py-18 grid gap-10 md:grid-cols-2">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 className="text-2xl font-bold text-white md:text-3xl">
              Team &amp; Expertise
            </h2>
            <p className="mt-2 text-gray-300">
              Our fabricators and installers are trained across the full project
              lifecycle—measurements, drawings, fabrication, finishing, and
              installation—with a focus on safety and clean workmanship.
            </p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {expertise.map((row, i) => (
                <motion.div
                  key={row.k}
                  custom={i}
                  variants={fadeUp}
                  className="rounded-xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 p-5"
                >
                  <div className="text-xs uppercase tracking-wide text-white/60">
                    {row.k}
                  </div>
                  <div className="mt-1 text-sm font-semibold text-white">
                    {row.v}
                  </div>
                </motion.div>
              ))}
            </div>

            <ul className="mt-6 space-y-3 text-sm text-gray-300">
              {[
                {
                  icon: Users,
                  text: "Qualified welders & fitters with site-safety training",
                },
                {
                  icon: Tool,
                  text: "Modern shop tools for accuracy and repeatability",
                },
                {
                  icon: Shield,
                  text: "Material & workmanship warranty available",
                },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.li
                    key={i}
                    custom={i}
                    variants={fadeUp}
                    className="flex items-center gap-2"
                  >
                    <Icon size={16} className="text-green-400" />
                    <span>{item.text}</span>
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>

          {/* Photo / Collage placeholder */}
          <motion.div
            initial={{ opacity: 0, x: 18 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-2xl border border-white/10"
          >
            <img
              src={aboutImg}
              alt="Workshop: fabrication and finishing area"
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-to-r from-green-500 to-yellow-500">
        <div
          className="absolute inset-0 opacity-10 mix-blend-multiply"
          style={{ backgroundImage: "url('/assets/pattern-noise.png')" }}
        />
        <div className="relative mx-auto max-w-7xl px-6 py-12 md:py-16">
          <div className="grid items-center gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-extrabold text-black md:text-3xl">
                Let’s bring your design to life.
              </h3>
              <p className="mt-1 text-black/80">
                Share measurements or book a site visit—quotes are free and
                fast.
              </p>
            </div>
            <div className="flex gap-3 md:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center rounded-lg bg-black px-5 py-3 font-semibold text-white hover:opacity-90"
              >
                Contact Us
              </Link>
              <a
                href="https://wa.me/256779706977"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-lg border border-black/20 bg-white/90 px-5 py-3 font-semibold text-black hover:bg-white"
              >
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </WebLayout>
  );
}
